<template>
  <v-dialog :value="value" max-width="480" scrollable @input="$emit('input', $event)">
    <v-card v-if="event" class="history-event">
      <v-card-title class="history-event__title">
        <div>
          <div class="text-overline">Histórico · somente leitura</div>
          <div class="text-h6">{{ patientName }}</div>
        </div>
        <v-spacer />
        <v-btn class="close-btn" icon aria-label="Fechar" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-calendar-text</v-icon>
              <span>Atendimento</span>
            </div>
          </div>
          <v-chip small :color="status.color" dark class="mb-4">
            <v-icon left small>{{ status.icon }}</v-icon>
            {{ status.label }}
          </v-chip>

          <div class="history-event__row">
            <v-icon small color="primary">mdi-calendar</v-icon>
            <span>{{ dateLabel }}</span>
          </div>
          <div class="history-event__row">
            <v-icon small color="primary">mdi-clock-outline</v-icon>
            <span>{{ timeLabel }}</span>
          </div>
          <div v-if="event.clinic" class="history-event__row">
            <v-icon small color="primary">mdi-map-marker</v-icon>
            <span>{{ event.clinic.name }}</span>
          </div>
          <div class="history-event__row">
            <v-icon small color="primary">mdi-stethoscope</v-icon>
            <span>{{ consultationType }}</span>
          </div>
        </section>
      </v-card-text>

      <v-card-actions v-if="canOpenRecord" class="ds-dialog-actions">
        <v-spacer />
        <v-btn
          v-if="event.appointment_id"
          color="primary"
          depressed
          @click="openAppointment"
        >
          <v-icon left>mdi-file-document-outline</v-icon>
          Abrir consulta
        </v-btn>
        <v-btn v-else color="primary" outlined @click="openPatientRecord">
          <v-icon left>mdi-account-file-outline</v-icon>
          Abrir prontuário
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatTime } from "@/utils/datetime";

const STATUS = {
  scheduled: { label: "Agendado", color: "blue", icon: "mdi-calendar-clock" },
  confirmed: {
    label: "Confirmado",
    color: "green",
    icon: "mdi-calendar-check",
  },
  waiting: { label: "Em espera", color: "amber darken-2", icon: "mdi-seat" },
  pending: {
    label: "Em atendimento",
    color: "orange",
    icon: "mdi-account-clock",
  },
  concluded: {
    label: "Atendido",
    color: "green darken-3",
    icon: "mdi-check-circle",
  },
  no_show: {
    label: "Falta",
    color: "blue-grey darken-1",
    icon: "mdi-account-off",
  },
  canceled: { label: "Cancelado", color: "red", icon: "mdi-close-circle" },
};

/**
 * Histórico do kanban — o card de um dia passado, só para leitura.
 *
 * Não reaproveita o CalendarEventDetailsDialog de propósito: aquele existe
 * para AGIR sobre o compromisso (remanejar, cancelar, iniciar atendimento), e
 * aqui nada disso faz sentido. Esconder botão por botão lá dentro deixaria uma
 * tela que muda de natureza conforme a origem.
 *
 * "Abrir consulta" leva à consulta clínica que nasceu deste agendamento
 * (`appointment_id`); sem a ligação — consulta antiga ambígua, ou paciente que
 * não foi atendido —, "Abrir prontuário". Os dois só para o médico: o
 * prontuário é `needsToBeADoctor` e a API barra a assistente (salvia-745).
 */
export default {
  name: "KanbanHistoryEventDialog",

  props: {
    value: { type: Boolean, default: false },
    event: { type: Object, default: null },
  },

  computed: {
    patientName() {
      return this.event?.patient?.name || "Sem paciente";
    },

    status() {
      return STATUS[this.event?.status] || STATUS.scheduled;
    },

    dateLabel() {
      if (!this.event?.start) return "";
      const text = new Date(this.event.start).toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    timeLabel() {
      return `${formatTime(this.event?.start)} – ${formatTime(
        this.event?.end
      )}`;
    },

    consultationType() {
      const match = String(this.event?.description || "").match(
        /Tipo de consulta:\s*([^-]+)/
      );
      return match ? match[1].trim() : "Consulta";
    },

    canOpenRecord() {
      return (
        this.$store.getters["auth/roleId"] === 1 && !!this.event?.patient?.id
      );
    },
  },

  methods: {
    openAppointment() {
      this.go({ appointment: String(this.event.appointment_id) });
    },

    openPatientRecord() {
      this.go();
    },

    go(query) {
      this.$emit("input", false);
      this.$router
        .push({
          path: `/patient-profile-dashboard/${this.event.patient.id}`,
          ...(query ? { query } : {}),
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.history-event__title {
  align-items: flex-start;
}

.history-event__row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.history-event__row:last-child {
  margin-bottom: 0;
}
</style>
